import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getExecutionReport,
  formatDuration,
  getJobTypeDisplayName,
  type ExecutionReportResponse,
  type ExecutionLogEntry,
} from "../services/schedulerAdminService";

// Group execution logs by user -> brokerage account -> strategies
interface StrategyGroup {
  log: ExecutionLogEntry;
}

interface AccountGroup {
  accountId: string | null;
  accountName: string;
  strategies: StrategyGroup[];
  succeeded: number;
  failed: number;
}

interface UserGroup {
  email: string;
  accounts: AccountGroup[];
  succeeded: number;
  failed: number;
  total: number;
}

function groupExecutionLogs(logs: ExecutionLogEntry[]): UserGroup[] {
  const userMap = new Map<string, UserGroup>();

  for (const log of logs) {
    const email = log.user_email || "Unknown User";
    const accountId = log.brokerage_account_id;
    const accountName = log.brokerage_account_name || "No Account";

    if (!userMap.has(email)) {
      userMap.set(email, { email, accounts: [], succeeded: 0, failed: 0, total: 0 });
    }
    const userGroup = userMap.get(email)!;

    let accountGroup = userGroup.accounts.find(
      (a) => a.accountId === accountId && a.accountName === accountName
    );
    if (!accountGroup) {
      accountGroup = { accountId, accountName, strategies: [], succeeded: 0, failed: 0 };
      userGroup.accounts.push(accountGroup);
    }

    accountGroup.strategies.push({ log });
    
    // Update counts
    if (log.status === "completed") {
      accountGroup.succeeded++;
      userGroup.succeeded++;
    } else if (log.status === "failed") {
      accountGroup.failed++;
      userGroup.failed++;
    }
    userGroup.total++;
  }

  return Array.from(userMap.values()).sort((a, b) => a.email.localeCompare(b.email));
}

export function ExecutionReportPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ExecutionReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!runId) return;
    setLoading(true);
    getExecutionReport(runId)
      .then((data) => {
        setReport(data);
        // Auto-expand all users
        const userEmails = new Set(data.execution_logs.map((l) => l.user_email || "Unknown User"));
        setExpandedUsers(userEmails);
        // Auto-expand all accounts  
        const accountKeys = new Set(
          data.execution_logs.map((l) => `${l.user_email || "Unknown User"}:${l.brokerage_account_name || "No Account"}`)
        );
        setExpandedAccounts(accountKeys);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [runId]);

  const groupedLogs = useMemo(() => {
    if (!report) return [];
    return groupExecutionLogs(report.execution_logs);
  }, [report]);

  const toggleUser = (email: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAccount = (key: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="glass-card p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-surface-400">Loading execution report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="glass-card p-8 text-center">
          <p className="text-red-400 mb-4">{error || "Report not found"}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary px-4 py-2">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { run, execution_logs, total_strategies, succeeded, failed, skipped } = report;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-surface-700/50 transition-colors"
        >
          <svg className="w-5 h-5 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">
            {getJobTypeDisplayName(run.job_type)} Report
          </h1>
          <p className="text-sm text-surface-400">
            {new Date(run.started_at).toLocaleString()} &middot; {run.trigger_type} &middot; {formatDuration(run.duration_ms)}
          </p>
        </div>
        <div className="ml-auto">
          <RunStatusBadge status={run.status} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Strategies" value={total_strategies} color="text-blue-400" />
        <SummaryCard label="Succeeded" value={succeeded} color="text-green-400" />
        <SummaryCard label="Failed" value={failed} color="text-red-400" />
        <SummaryCard label="Skipped" value={skipped} color="text-yellow-400" />
      </div>

      {/* Run Summary (from JSONB) */}
      {run.summary && Object.keys(run.summary).length > 0 && (
        <div className="glass-card p-4 mb-6">
          <h2 className="text-sm font-medium text-surface-300 mb-3">Run Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(run.summary).map(([key, value]) => (
              <div key={key} className="bg-surface-800/50 rounded-lg p-3">
                <p className="text-xs text-surface-500 mb-1">{formatKey(key)}</p>
                <p className="text-sm text-surface-200 font-medium">
                  {typeof value === "number" ? value.toLocaleString() : String(value ?? "-")}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {run.error_message && (
        <div className="glass-card p-4 mb-6 border border-red-500/30">
          <h2 className="text-sm font-medium text-red-400 mb-2">Error</h2>
          <pre className="text-sm text-red-300 whitespace-pre-wrap font-mono">{run.error_message}</pre>
        </div>
      )}

      {/* Execution Logs - Hierarchical View */}
      <div className="glass-card p-4">
        <h2 className="text-sm font-medium text-surface-300 mb-4">
          Strategy Execution Details ({execution_logs.length})
        </h2>

        {execution_logs.length === 0 ? (
          <p className="text-surface-500 text-sm py-4 text-center">
            No execution logs linked to this run.
            {run.status === "completed" && " This run may have occurred before the scheduler_run_id linkage was deployed."}
          </p>
        ) : (
          <div className="space-y-2">
            {groupedLogs.map((userGroup) => (
              <UserGroupRow
                key={userGroup.email}
                userGroup={userGroup}
                expanded={expandedUsers.has(userGroup.email)}
                onToggle={() => toggleUser(userGroup.email)}
                expandedAccounts={expandedAccounts}
                onToggleAccount={toggleAccount}
                expandedLog={expandedLog}
                onToggleLog={(id) => setExpandedLog(expandedLog === id ? null : id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Correlation ID */}
      <div className="mt-6 text-center px-4">
        <p className="text-xs text-surface-500 break-all">
          Correlation ID: <code className="text-surface-400">{run.correlation_id}</code>
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="glass-card p-4 text-center">
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-surface-500 mt-1">{label}</p>
    </div>
  );
}

function RunStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: "bg-green-500/20 text-green-400",
    failed: "bg-red-500/20 text-red-400",
    running: "bg-yellow-500/20 text-yellow-400",
  };
  return (
    <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${colors[status] || "bg-surface-600/20 text-surface-400"}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function UserGroupRow({
  userGroup,
  expanded,
  onToggle,
  expandedAccounts,
  onToggleAccount,
  expandedLog,
  onToggleLog,
}: {
  userGroup: UserGroup;
  expanded: boolean;
  onToggle: () => void;
  expandedAccounts: Set<string>;
  onToggleAccount: (key: string) => void;
  expandedLog: string | null;
  onToggleLog: (id: string) => void;
}) {
  const hasFailed = userGroup.failed > 0;
  
  return (
    <div className="bg-surface-800/30 rounded-lg border border-surface-700/50 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-surface-700/30 transition-colors text-left"
      >
        <svg
          className={`w-4 h-4 text-surface-500 transition-transform flex-shrink-0 ${expanded ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>

        <svg className="w-5 h-5 text-surface-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-surface-200 font-medium truncate">{userGroup.email}</p>
          <p className="text-xs text-surface-500">{userGroup.accounts.length} account{userGroup.accounts.length !== 1 ? "s" : ""}</p>
        </div>

        <div className="flex items-center gap-2">
          {hasFailed ? (
            <span className="text-xs font-medium text-yellow-400">
              {userGroup.succeeded}/{userGroup.total} ({userGroup.failed} failed)
            </span>
          ) : (
            <span className="text-xs font-medium text-green-400">
              {userGroup.succeeded}/{userGroup.total}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-surface-700/30 pl-6">
          {userGroup.accounts.map((account) => {
            const accountKey = `${userGroup.email}:${account.accountName}`;
            return (
              <AccountGroupRow
                key={accountKey}
                account={account}
                expanded={expandedAccounts.has(accountKey)}
                onToggle={() => onToggleAccount(accountKey)}
                expandedLog={expandedLog}
                onToggleLog={onToggleLog}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function AccountGroupRow({
  account,
  expanded,
  onToggle,
  expandedLog,
  onToggleLog,
}: {
  account: AccountGroup;
  expanded: boolean;
  onToggle: () => void;
  expandedLog: string | null;
  onToggleLog: (id: string) => void;
}) {
  const hasFailed = account.failed > 0;
  const total = account.strategies.length;

  return (
    <div className="border-b border-surface-700/20 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-2.5 hover:bg-surface-700/20 transition-colors text-left"
      >
        <svg
          className={`w-3.5 h-3.5 text-surface-500 transition-transform flex-shrink-0 ${expanded ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>

        <svg className="w-4 h-4 text-surface-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-surface-300 truncate">{account.accountName}</p>
          <p className="text-xs text-surface-500">{total} strateg{total !== 1 ? "ies" : "y"}</p>
        </div>

        <div className="flex items-center gap-2">
          {hasFailed ? (
            <span className="text-xs font-medium text-yellow-400">
              {account.succeeded}/{total}
            </span>
          ) : (
            <span className="text-xs font-medium text-green-400">
              {account.succeeded}/{total}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="pl-6 pb-1">
          {account.strategies.map(({ log }) => (
            <ExecutionLogRow
              key={log.id}
              log={log}
              expanded={expandedLog === log.id}
              onToggle={() => onToggleLog(log.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ExecutionLogRow({
  log,
  expanded,
  onToggle,
}: {
  log: ExecutionLogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const statusColors: Record<string, string> = {
    completed: "text-green-400",
    failed: "text-red-400",
    skipped: "text-yellow-400",
    pending: "text-blue-400",
  };

  return (
    <div className="bg-surface-800/50 rounded-lg border border-surface-700/50 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-3 hover:bg-surface-700/30 transition-colors text-left"
      >
        <svg
          className={`w-4 h-4 text-surface-500 transition-transform ${expanded ? "rotate-90" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>

        <div className="flex-1 min-w-0">
          <p className="text-sm text-surface-200 font-medium truncate">
            {log.strategy_name || "Unknown Strategy"}
          </p>
          <p className="text-xs text-surface-500 truncate">
            {log.investment_name || log.investment_id}
          </p>
        </div>

        <span className={`text-xs font-medium ${statusColors[log.status] || "text-surface-400"}`}>
          {log.status}
        </span>

        {log.duration_ms != null && (
          <span className="text-xs text-surface-500">{log.duration_ms}ms</span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-surface-700/30 space-y-3">
          {/* Meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3">
            <MiniDetail label="Portfolio Value" value={`$${log.portfolio_value_before.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
            <MiniDetail label="Cash Balance" value={`$${log.cash_balance_before.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} />
            <MiniDetail label="Scale Factor" value={log.scale_factor} />
            <MiniDetail label="Executed" value={new Date(log.execution_date).toLocaleTimeString()} />
          </div>

          {/* Error */}
          {log.error_message && (
            <div className="bg-red-500/10 rounded p-2 border border-red-500/20">
              <p className="text-xs text-red-300 font-mono">{log.error_message}</p>
            </div>
          )}

          {/* Intended Trades */}
          <JsonSection title="Intended Trades" data={log.intended_trades} />

          {/* Orders Executed */}
          <JsonSection title="Orders Executed" data={log.orders_executed} />

          {/* Orders Failed */}
          <JsonSection title="Orders Failed" data={log.orders_failed} />

          {/* Drift Detected */}
          <JsonSection title="Drift Detected" data={log.drift_detected} />
        </div>
      )}
    </div>
  );
}

function MiniDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-surface-500">{label}</p>
      <p className="text-sm text-surface-300 font-medium">{value}</p>
    </div>
  );
}

function JsonSection({ title, data }: { title: string; data: unknown }) {
  if (!data || (typeof data === "string" && data === "null") || (Array.isArray(data) && data.length === 0)) {
    return null;
  }

  const formatted = typeof data === "string" ? data : JSON.stringify(data, null, 2);

  return (
    <div>
      <p className="text-xs text-surface-500 mb-1">{title}</p>
      <pre className="text-xs text-surface-300 font-mono bg-surface-900/50 rounded p-2 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
        {formatted}
      </pre>
    </div>
  );
}

function formatKey(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
